/**
 * Text normalisation helpers shared across patient-facing forms.
 *
 * Why a single utility? Because the user has explicitly asked for ALL
 * patient name inputs to render in strict Title Case — first letter of
 * every word uppercase, the rest lowercase — regardless of how many
 * names or surnames the patient has. Keeping the logic in one place
 * avoids drift between the admin "Nuevo Paciente" dialog, the public
 * booking form, the agenda dialogs and the clinical-history editor.
 */

/**
 * Convert a person name to strict Title Case.
 * - Trims and collapses internal whitespace.
 * - Lowercases everything, then capitalises the first letter of each
 *   space-separated word.
 * - Preserves accents (`á → Á`, `ñ → Ñ`) because JS `toUpperCase`
 *   handles them correctly when the source is UTF-8.
 * - Does NOT lowercase prepositions like "de"/"del" — the user wants
 *   strict capitalisation per word ("María De Los Ángeles"). If we
 *   need Spanish-style lowercase particles later, gate it behind an
 *   explicit flag.
 *
 * @param {string} input
 * @returns {string}
 */
export function toTitleCaseName(input) {
  if (typeof input !== 'string') return '';
  const trimmed = input.replace(/\s+/g, ' ').trim();
  if (!trimmed) return '';
  return trimmed
    .toLocaleLowerCase('es-ES')
    .split(' ')
    .map((word) => {
      if (!word) return '';
      // Handle hyphenated surnames like "García-López" → "García-López".
      if (word.includes('-')) {
        return word
          .split('-')
          .map((piece) => (piece ? piece[0].toLocaleUpperCase('es-ES') + piece.slice(1) : ''))
          .join('-');
      }
      return word[0].toLocaleUpperCase('es-ES') + word.slice(1);
    })
    .join(' ');
}

/**
 * Live-input variant of {@link toTitleCaseName}.
 *
 * Why a separate function? The strict version trims and collapses
 * whitespace, which fights the user while they're still typing — they
 * can't add a trailing space to start a new surname without it being
 * eaten. This version capitalises the first letter of every word but
 * preserves whitespace exactly so it can be wired directly to `onChange`
 * without disrupting typing or cursor position.
 *
 * Handles pasted uppercase blobs (e.g. "JUAN PÉREZ" → "Juan Pérez") and
 * mixed-case inputs ("jUAN pErEZ" → "Juan Pérez") in real time.
 *
 * @param {string} input
 * @returns {string}
 */
export function toTitleCaseNameLive(input) {
  if (typeof input !== 'string' || input.length === 0) return '';
  const lower = input.toLocaleLowerCase('es-ES');
  // Capitalise the first letter at the start of the string or after any
  // whitespace / hyphen. Uses Unicode letter class so accents and ñ are
  // captured as "letters" rather than skipped.
  return lower.replace(/(^|[\s-])(\p{L})/gu, (_m, sep, ch) => sep + ch.toLocaleUpperCase('es-ES'));
}
